import React from 'react';

import { SORT } from '$gbusiness/enums';
import CellModel from '$gbusiness/models/cell';
import { IconButton, Tooltip } from '@mui/material';
import {
  MoreHoriz,
  Star,
  StarOutline,
  BookmarkOutlined,
  BookmarkBorderOutlined,
  VisibilityOutlined,
  VisibilityOff,
} from '@mui/icons-material';
import { timestamp } from '$gbusiness/helpers/date';
import intl from '$intl';
import Img from '$gcomponents/primitives/img';
import { Link } from '$gcomponents/primitives';
import { FACTORY_TYPE } from '$fbusiness/enums/options/factoryType';
import { TAX_TYPE } from '$fbusiness/enums/options/taxType';
import Breadcrumbs from '$fcomponents/breadcrumbs';
import { getInitial, percentage } from '$gbusiness/helpers/util';
import PATH from '$business/enums/paths';
import { accessHide, getAccess } from '$fbusiness/helpers/util';
import Barcode from '$fcomponents/barcode';

export const defaultItemColumns = [
  'sku',
  'categoryName',
  'price',
  'wholesalePrice',
  'tax',
  'distributions',
  'quantity',
];

export const PRODUCTS_TABLE = (
  factory,
  onToggleHide,
  onToggleEbrochure,
  onTogglePromo,
  currentState,
  isLowStockMode = false,
): Array<CellModel> => {
  const ACCESS = getAccess(currentState);
  return [
    {
      // Sorting by this column returns the list to its saved order, which is the
      // only state in which rows can be dragged. Without it a user who sorts by
      // another column has no way back -- the table persists sort to
      // localStorage, so it survives a reload too.
      label: 'SCREEN.ITEMS.COLS.ORDER',
      value: 'rank',
      align: 'center',
      sortable: SORT.ASC,
      // Not hideable: reordering depends on this column being sortable, so it must
      // not be something a user can switch off from the column picker.
      alwaysShow: true,
      component: (row) => <>{Number(row.rank) > 0 ? Math.round(Number(row.rank)) : '-'}</>,
      width: 90,
    },
    {
      label: 'SCREEN.ITEMS.COLS.PHOTO',
      value: 'itemId',
      className: 'image',
      align: 'center',
      component: (row) => {
        const thumbImage = row.itemImage || row.image?.sizes?.small;
        return (
          row.itemImage && (
            <Img
              src={thumbImage || ''}
              cache={timestamp(row.updatedAt)}
              width="64px"
              height="64px"
              placeholder="No Image"
              initial={currentState?.settings?.imageInitial ? getInitial(row.name) : undefined}
              alt="dept img"
            />
          )
        );
      },
      width: 150,
    },
    {
      label: 'SCREEN.ITEMS.COLS.SKU',
      value: 'sku',
      className: 'desktop',
      sortable: SORT.ASC,
      width: 160,
    },
    {
      label: 'SCREEN.ITEMS.COLS.BARCODE',
      value: 'barcode',
      className: 'desktop',
      component: (row) => {
        if (row.barcode) {
          return <Barcode value={row.barcode} width={1} height={28} displayValue />;
        }
        return null;
      },
      sortable: SORT.ASC,
      width: 160,
    },
    {
      label: 'SCREEN.ITEMS.COLS.CATEGORY',
      value: 'categoryName',
      className: 'vertical',
      align: 'left',
      component: (row) => {
        if (factory.settings?.subcategory && row.categories && row.categories.length) {
          const category = row.categories[0];
          return (
            <Breadcrumbs
              noLink
              separator="›"
              excludeHome
              breadcrumbs={category.breadcrumbs}
              current={category}
            />
          );
        }
        return row?.categories && row.categories.map((d) => <div key={d.id}>{d.name}</div>);
      },
      sortable: SORT.ASC,
      width: 200,
    },
    {
      label: 'SCREEN.ITEMS.COLS.NAME',
      value: 'name',
      className: 'main',
      component: (row) => <Link route={PATH.ITEMS + '/' + row.id}>{row.name}</Link>,
      alwaysShow: true,
      sortable: SORT.ASC,
      width: 250,
    },
    {
      label: 'SCREEN.ITEMS.COLS.VENDOR',
      value: 'vendor',
      sortable: SORT.ASC,
      alwaysShow: isLowStockMode,
      width: 250,
    },
    {
      label: 'SCREEN.ITEMS.COLS.RETAIL_PRICE',
      value: 'price',
      align: 'center',
      component: (row) => <>{intl('PRICE', { amount: row.retailPrice })}</>,
      sortable: SORT.ASC,
      width: 120,
    },
    {
      label: 'SCREEN.ITEMS.COLS.PRICE',
      value: 'wholesalePrice',
      align: 'center',
      component: (row) => <>{intl('PRICE', { amount: row.wholesalePrice })}</>,
      sortable: SORT.ASC,
      width: 120,
    },
    {
      label: 'SCREEN.ITEMS.COLS.RAW_COST',
      value: 'rawCost',
      align: 'center',
      component: (row) => <>{intl('PRICE', { amount: row.rawCost })}</>,
      sortable: SORT.ASC,
      width: 120,
    },
    {
      label: 'SCREEN.ITEMS.COLS.TAX',
      value: 'tax',
      align: 'center',
      component: (row) => (
        <>
          {!row.taxId
            ? 'No Tax'
            : row.tax?.taxType === TAX_TYPE.UNIVERSAL
            ? percentage(row.tax?.amount || 0)
            : TAX_TYPE.STORE}
        </>
      ),
      width: 120,
    },
    ...(factory?.settings?.inventory
      ? factory?.warehouses?.map((warehouse, i) => ({
          labelText: warehouse.short,
          value: `distributions[${i}].quantity`,
          align: 'center',
          className: `desktop'${i === 0 ? ' first ' : ''}${
            i === factory.warehouses.length - 1 ? ' last' : ''
          }`,
          component: (row) => {
            const warehouseQty =
              row.distributions?.find((d) => d.warehouseId === warehouse.id)?.quantity || 0;
            return (
              <span className={`${row.warehouseId === warehouse.id ? 'bold' : ''}`}>
                {warehouseQty == null ? 'Unlimited' : warehouseQty}
              </span>
            );
          },
          sortable: SORT.ASC,
          width: 60,
        }))
      : []),
    // {
    //   label: 'SCREEN.ITEMS.COLS.QUANTITY' + (factory?.settings?.inventory ? '_SUM' : ''),
    //   value: 'totalQuantity',
    //   align: 'center',
    //   className: 'desktop' + (factory?.settings?.inventory ? ' last' : ''),
    //   component: (row) => {
    //     if (factory?.settings?.inventory) {
    //       return row.quantity || 0;
    //     }
    //     return <>{row.quantity == null ? 'Unlimited' : row.quantity}</>;
    //   },
    //   sortable: SORT.ASC,
    //   width: factory?.settings?.inventory ? 80 : 120,
    // },
    {
      label: 'SCREEN.ITEMS.COLS.QUANTITY',
      value: 'quantity',
      align: 'center',
      className: 'desktop',
      component: (row) => (
        <span className={row.lowStock > 0 && row.quantity <= row.lowStock ? 'error' : ''}>
          {row.quantity == null ? 'Unlimited' : row.quantity}
        </span>
      ),
      sortable: SORT.ASC,
      width: 120,
    },
    ...(factory?.settings.lowStock
      ? [
          {
            label: 'SCREEN.ITEMS.COLS.TARGET_STOCK',
            value: 'targetStock',
            align: 'center',
            className: 'desktop',
            component: (row) => <>{row.targetStock == null ? '' : row.targetStock}</>,
            width: 120,
          },
          {
            label: 'SCREEN.ITEMS.COLS.REORDER_QTY',
            value: 'targetStock',
            align: 'center',
            isHidden: !isLowStockMode,
            component: (row) => (row.targetStock ? row.targetStock - row.quantity : ''),
            alwaysShow: isLowStockMode,
            width: 120,
          },
        ]
      : []),
    {
      isHidden: factory.type !== FACTORY_TYPE.ECOMMERCE,
      label: 'SCREEN.ITEMS.COLS.PACKING',
      value: 'settings.packing',
      align: 'center',
      className: 'desktop',
      sortable: SORT.ASC,
      width: 120,
    },
    {
      isHidden: factory.type !== FACTORY_TYPE.ECOMMERCE,
      label: 'SCREEN.ITEMS.COLS.SKID',
      value: 'settings.skid',
      align: 'center',
      className: 'desktop',
      sortable: SORT.ASC,
      width: 120,
    },
    {
      isHidden: factory.type !== FACTORY_TYPE.ECOMMERCE,
      label: 'SCREEN.ITEMS.COLS.LOTNO',
      value: 'settings.lotNo',
      align: 'center',
      className: 'desktop',
      sortable: SORT.ASC,
      width: 120,
    },
    {
      isHidden: factory.type !== FACTORY_TYPE.ECOMMERCE,
      label: 'SCREEN.ITEMS.COLS.BINNO',
      value: 'settings.binNo',
      align: 'center',
      className: 'desktop',
      sortable: SORT.ASC,
      width: 120,
    },
    {
      label: 'SCREEN.ITEMS.COLS.ACTION',
      value: '',
      className: 'action',
      align: 'center',
      alwaysShow: true,
      component: (row, actions) => {
        return (
          <div className="item-actions">
            <Tooltip
              title={intl('SCREEN.ITEM.TOOLTIP.EBROCHURE')}
              className={accessHide(ACCESS.ACTION.ITEM.EDIT)}>
              <IconButton size="small" onClick={() => onToggleEbrochure(row)}>
                {row.ebrochure ? (
                  <BookmarkOutlined color="primary" />
                ) : (
                  <BookmarkBorderOutlined color="disabled" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip
              title={intl('SCREEN.ITEM.TOOLTIP.PROMO')}
              className={accessHide(ACCESS.ACTION.ITEM.EDIT)}>
              <IconButton size="small" onClick={() => onTogglePromo(row)}>
                {row.settings.promo ? <Star color="primary" /> : <StarOutline color="disabled" />}
              </IconButton>
            </Tooltip>
            <Tooltip
              title={intl('SCREEN.ITEM.TOOLTIP.HIDDEN')}
              className={accessHide(ACCESS.ACTION.ITEM.EDIT)}>
              <IconButton size="small" onClick={() => onToggleHide(row)}>
                {!row.hide ? <VisibilityOutlined color="disabled" /> : <VisibilityOff color="error" />}
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={(e) => actions.handlePopoverOpen(e, { ...row, route: PATH.ITEMS })}>
              <MoreHoriz />
            </IconButton>
          </div>
        );
      },
      width: 150,
      minWidth: 150,
    },
  ];
};
